var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * CZNA-1471 — a FREE-TEXT subfield with a vocabulary quick pick (`suggestVocabularyType`).
 *
 * While the cataloguer types, the terms of the vocabulary that begin with the typed text are
 * offered under the input. A pick puts the term's text into the field, leaves the caret after
 * it and keeps the focus, so the value goes on: NK cites a source as its preset designation
 * followed by the details, `LC (Names), 2. 3. 2020`, and only the designation is the term.
 *
 * What this is NOT, on purpose: a picker. The value stays plain text — no `sourceAuthorityId`
 * is written, nothing is restricted, and anything may be typed without a pick. A vocabulary
 * bound as `vocabularyType` would replace the value with the term and make the details
 * impossible to add, which is the problem the ticket names.
 *
 * Matching is by prefix, case-insensitive: the term is the BEGINNING of the value, and once
 * the cataloguer types past it ("LC (Names), 2") nothing matches and the list closes by
 * itself. The whole register is read once per type, page by page (NK's lists are ~200 terms,
 * one page; the endpoint caps a page at 500), and filtered here, so typing costs no request.
 *
 * CZNA-1523: the prefix match cannot find a term whose beginning the cataloguer does not
 * know, so a magnifier next to the input opens the whole register, searched anywhere in the
 * name (`VocabularyLookupModal`). Its pick does exactly what a quick pick does.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { IconButton } from '@folio/stripes/components';
import { Controller } from 'react-hook-form';
import { useOkapiKy } from '@folio/stripes/core';
import { useQuery } from 'react-query';
import { resolveLabel } from '../renderer/labelResolver';
import { fetchVocabularies } from '../../../api/vocabularyService';
import ClearFieldButton from './ClearFieldButton';
import VocabularyLookupModal from './VocabularyLookupModal';
import { subfieldRowStyle, subfieldCodeStyle, subfieldLabelStyle, subfieldInputStyle, subfieldActionsStyle, } from './subfieldStyles';
/** At most this many suggestions are shown at once. */
export var MAX_SUGGESTIONS = 10;
/** The endpoint's page cap (`VocabularyController`, `@Max(500)`). */
var TERM_PAGE = 500;
/** The endpoint's offset cap (`VocabularyController`, `@Max(10000)`); a page past it is a 400. */
var MAX_OFFSET = 10000;
/**
 * Every term of `type`, in register order. Paged rather than one request because the admin
 * may attach any open vocabulary, and one capped page would silently drop the rest.
 */
export var fetchAllTermNames = function (ky, type) { return __awaiter(void 0, void 0, void 0, function () {
    var names, offset, page, items;
    var _a, _b;
    return __generator(this, function (_c) {
        switch (_c.label) {
            case 0:
                names = [];
                offset = 0;
                _c.label = 1;
            case 1:
                if (!(offset <= MAX_OFFSET)) return [3 /*break*/, 4];
                return [4 /*yield*/, fetchVocabularies(ky, {
                        type: type,
                        sortBy: 'headingValue', sortOrder: 'asc', limit: TERM_PAGE,
                        offset: offset,
                    })];
            case 2:
                page = _c.sent();
                items = (_a = page.items) !== null && _a !== void 0 ? _a : [];
                items.forEach(function (item) { if (item.name)
                    names.push(item.name); });
                if (items.length < TERM_PAGE || offset + items.length >= ((_b = page.totalRecords) !== null && _b !== void 0 ? _b : 0))
                    return [2 /*return*/, names];
                _c.label = 3;
            case 3:
                offset += TERM_PAGE;
                return [3 /*break*/, 1];
            case 4: return [2 /*return*/, names];
        }
    });
}); };
/**
 * Terms that begin with `typed` (case-insensitive), in register order, without the one the
 * value already equals — offering what is already there would keep the list open for nothing.
 */
export var matchSuggestions = function (terms, typed) {
    var needle = typed.trim().toLocaleLowerCase('cs');
    if (!needle)
        return [];
    return terms
        .filter(function (term) {
        var lower = term.toLocaleLowerCase('cs');
        return lower.startsWith(needle) && lower !== needle;
    })
        .slice(0, MAX_SUGGESTIONS);
};
var NO_TERMS = [];
var wrapperStyle = { position: 'relative', flex: 1, display: 'flex' };
var listStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 10,
    margin: '2px 0 0',
    padding: '4px 0',
    listStyle: 'none',
    background: '#fff',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
    maxHeight: '280px',
    overflowY: 'auto',
};
var optionStyle = function (active) { return ({
    padding: '4px 10px',
    fontSize: '14px',
    cursor: 'pointer',
    background: active ? '#e6f0fa' : undefined,
}); };
var SuggestInput = function (_a) {
    var sf = _a.sf, inputId = _a.inputId, label = _a.label, value = _a.value, terms = _a.terms, termsLoading = _a.termsLoading, termsError = _a.termsError, onChange = _a.onChange, onBlur = _a.onBlur, onClear = _a.onClear;
    var intl = useIntl();
    var inputRef = useRef(null);
    var _b = useState(false), open = _b[0], setOpen = _b[1];
    var _c = useState(false), lookupOpen = _c[0], setLookupOpen = _c[1];
    var _d = useState(-1), active = _d[0], setActive = _d[1];
    // Set by a pick, consumed after the re-render that carries the new value: only then is the
    // caret placed, or the browser would put it back where the old value ended.
    var caretPending = useRef(false);
    var suggestions = useMemo(function () { return matchSuggestions(terms, value); }, [terms, value]);
    var listId = "".concat(inputId, "-suggestions");
    var expanded = open && suggestions.length > 0;
    useEffect(function () {
        if (!caretPending.current || !inputRef.current)
            return;
        caretPending.current = false;
        var end = inputRef.current.value.length;
        inputRef.current.focus();
        inputRef.current.setSelectionRange(end, end);
    }, [value]);
    var pick = function (term) {
        caretPending.current = true;
        onChange(term);
        setOpen(false);
        setActive(-1);
    };
    // The modal hands focus back as it unmounts, so the caret is placed only after that —
    // a value equal to the old one would not run the effect above either.
    var pickFromLookup = function (term) {
        setLookupOpen(false);
        onChange(term);
        setTimeout(function () {
            var el = inputRef.current;
            if (!el)
                return;
            el.focus();
            el.setSelectionRange(el.value.length, el.value.length);
        }, 0);
    };
    var handleKeyDown = function (e) {
        if (!expanded) {
            if (e.key === 'ArrowDown' && suggestions.length > 0) {
                setOpen(true);
                setActive(0);
                e.preventDefault();
            }
            return;
        }
        if (e.key === 'ArrowDown') {
            setActive(function (i) { return (i + 1) % suggestions.length; });
            e.preventDefault();
        }
        else if (e.key === 'ArrowUp') {
            setActive(function (i) { return (i <= 0 ? suggestions.length - 1 : i - 1); });
            e.preventDefault();
        }
        else if (e.key === 'Enter' && active >= 0) {
            // Enter on a highlighted row picks it — and must not submit the form around it.
            e.preventDefault();
            pick(suggestions[active]);
        }
        else if (e.key === 'Escape') {
            setOpen(false);
            setActive(-1);
        }
    };
    return (React.createElement("div", { style: subfieldRowStyle },
        !sf.formOnly && React.createElement("span", { style: subfieldCodeStyle, "aria-hidden": "true" },
            "$",
            sf.code),
        React.createElement("label", { htmlFor: inputId, style: subfieldLabelStyle },
            label,
            sf.required && React.createElement("span", { style: { color: '#d0021b', marginLeft: '2px' }, "aria-hidden": "true" }, "*")),
        React.createElement("div", { style: wrapperStyle },
            React.createElement("input", { ref: inputRef, id: inputId, type: "text", role: "combobox", "aria-autocomplete": "list", "aria-expanded": expanded, "aria-controls": listId, "aria-activedescendant": expanded && active >= 0 ? "".concat(listId, "-").concat(active) : undefined, autoComplete: "off", value: value, placeholder: sf.placeholder, "aria-required": sf.required, onChange: function (e) {
                    onChange(e.target.value);
                    setOpen(true);
                    setActive(-1);
                }, onFocus: function () { return setOpen(true); }, onBlur: function () {
                    setOpen(false);
                    setActive(-1);
                    onBlur();
                }, onKeyDown: handleKeyDown, style: __assign(__assign({}, subfieldInputStyle), { width: '100%' }) }),
            expanded && (React.createElement("ul", { id: listId, role: "listbox", "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.suggest.listLabel' }), style: listStyle, "data-testid": "".concat(inputId, "-suggestions") }, suggestions.map(function (term, i) { return (
            // The keyboard stays on the input, which walks the options (aria-activedescendant).
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            React.createElement("li", { key: term, id: "".concat(listId, "-").concat(i), role: "option", "aria-selected": i === active, style: optionStyle(i === active), 
                // mousedown would blur the input first and close the list under the click.
                onMouseDown: function (e) { return e.preventDefault(); }, onClick: function () { return pick(term); }, onMouseEnter: function () { return setActive(i); } }, term)); })))),
        React.createElement("span", { style: subfieldActionsStyle },
            React.createElement(ClearFieldButton, { visible: !!value, onClear: onClear }),
            React.createElement(IconButton, { icon: "search", onClick: function () { return setLookupOpen(true); }, "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.vocabularyLookup.button' }) })),
        lookupOpen && (React.createElement(VocabularyLookupModal, { open: true, onClose: function () { return setLookupOpen(false); }, isLoading: termsLoading, isError: termsError, terms: terms, onSelect: pickFromLookup }))));
};
var VocabularySuggestSubfield = function (_a) {
    var _b;
    var sf = _a.subfieldDef, pathPrefix = _a.pathPrefix, control = _a.control, inputId = _a.inputId, onBlurValue = _a.onBlurValue, onClear = _a.onClear;
    var intl = useIntl();
    var ky = useOkapiKy();
    var type = (_b = sf.suggestVocabularyType) !== null && _b !== void 0 ? _b : '';
    // A failed read leaves the input working as plain text — the quick pick is a convenience,
    // never a gate, so there is nothing to report in the form.
    var _c = useQuery(['czna-vocabulary-suggest', type], function () { return fetchAllTermNames(ky, type); }, { enabled: !!type, staleTime: 5 * 60 * 1000 }), data = _c.data, isLoading = _c.isLoading, isError = _c.isError;
    var terms = data !== null && data !== void 0 ? data : NO_TERMS;
    return (React.createElement(Controller, { key: sf.code, name: "".concat(pathPrefix, ".").concat(sf.code, ".value"), control: control, render: function (_a) {
            var field = _a.field;
            return (React.createElement(SuggestInput, { sf: sf, inputId: inputId, label: resolveLabel(sf, intl), value: field.value || '', terms: terms, termsLoading: isLoading, termsError: isError, onChange: function (next) { return field.onChange(next); }, onBlur: function () {
                    field.onBlur();
                    onBlurValue === null || onBlurValue === void 0 ? void 0 : onBlurValue(field.value || '');
                }, onClear: function () {
                    field.onChange('');
                    onClear === null || onClear === void 0 ? void 0 : onClear();
                } }));
        } }));
};
export default VocabularySuggestSubfield;
